import { ColumnSort } from "@tanstack/react-table";

import {
  BackfillSINEncryptionType,
  CreateStudentType,
  EdgeFunctionsEnum,
  UnwrapStudentDEKType,
  UpdateStudentType,
} from "@libs/schemas";
import { DEFAULT_PAGE_SIZE } from "@/domain/constants";
import { StudentFilterType, StudentWithEnrolmentsType } from "@/domain/student";
import { sendSentryError } from "@/services/sentry";
import { invokeEdgeFunction, supabaseClient } from "@/services/supabase";
import { encryptWithDEK } from "@/utils/crypto";
import { paginationRange } from "@/utils/helpers";

export const StudentApi = {
  getStudent: async (studentId?: string | null) => {
    if (!studentId) {
      return;
    }

    const { data, error } = await supabaseClient
      .from("students")
      .select("*")
      .eq("id", studentId)
      .single();

    if (error) {
      sendSentryError({ message: "Error getting student", error });
    }

    return data;
  },
  updateStudent: async ({
    studentId,
    params,
  }: {
    studentId: string;
    params: Omit<UpdateStudentType, "id">;
  }) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.UpdateStudent,
      opts: {
        body: { ...params, id: studentId },
      },
    });

    if (error || !data) {
      const errorMessage = error?.message;

      sendSentryError({
        message: "Error updating student",
        error: errorMessage,
      });
    }

    return data;
  },

  updateStudentPartial: async ({
    studentId,
    params,
    dekBase64,
  }: {
    studentId: string;
    params: Partial<Omit<UpdateStudentType, "id">>;
    dekBase64?: string | null;
  }) => {
    const { sin_number, ...safeParams } = params;
    let encrypted_sin: string | undefined;

    if (sin_number) {
      if (!dekBase64) {
        throw new Error("Missing DEK for SIN encryption");
      }

      encrypted_sin = await encryptWithDEK({
        dekBase64,
        plaintext: sin_number,
      });
    }

    const { data, error } = await supabaseClient
      .from("students")
      .update({
        ...safeParams,
        ...(encrypted_sin ? { encrypted_sin } : {}),
      })
      .eq("id", studentId)
      .select()
      .single();

    if (error) {
      sendSentryError({ message: "Error updating student", error });
    }

    return data;
  },

  listStudents: async ({
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort,
    filters,
    needsPagination = true,
  }: {
    page: number;
    pageSize?: number;
    sort?: ColumnSort;
    filters?: StudentFilterType;
    needsPagination?: boolean;
  }) => {
    const range = paginationRange(page, pageSize);

    const query = supabaseClient.from("students").select("*");

    // Add search filter if provided
    const searchTerm = filters?.search?.trim() ?? "";
    if (searchTerm.length) {
      // Use Supabase 'ilike' operator for searching student names, student number, and email
      query.or(
        `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,student_number.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`,
      );
    }
    // Only filter by status if statuses are explicitly provided and the array is not empty
    if (filters?.statuses?.length) {
      query.in("status", filters.statuses);
    }

    if (sort) {
      if (sort.id === "fullname") {
        query.order("first_name", { ascending: !sort.desc });
        query.order("last_name", { ascending: !sort.desc });
      } else {
        query.order(sort.id, { ascending: !sort.desc });
      }
    } else {
      query.order("created_at", { ascending: true });
    }

    if (needsPagination) {
      query.range(range.from, range.to);
    }

    const { data, error } = await query;
    if (error) {
      sendSentryError({ message: "Error getting student list", error });
    }

    return data ?? [];
  },

  countStudents: async ({ filters }: { filters?: StudentFilterType }) => {
    const query = supabaseClient
      .from("students")
      .select("id", { count: "exact" });

    // Add search filter if provided
    const searchTerm = filters?.search?.trim() ?? "";
    if (searchTerm.length) {
      // Use Supabase 'ilike' operator for searching student names, student number, and email
      query.or(
        `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,student_number.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`,
      );
    }

    if (filters?.statuses?.length) {
      query.in("status", filters.statuses);
    }

    const { count, error } = await query;

    if (error) {
      sendSentryError({ message: "Error counting students", error });
      throw error;
    }

    return count ?? 0;
  },
  createStudent: async (params: CreateStudentType) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.CreateStudent,
      opts: {
        body: params,
      },
    });

    if (error || !data) {
      const errorMessage = error?.message;

      sendSentryError({
        message: "Error creating student",
        error: errorMessage,
      });
    }

    return data;
  },

  listStudentsAsOptions: async ({
    sort,
    filters,
  }: {
    sort?: ColumnSort;
    filters?: StudentFilterType;
  }) => {
    const PAGE_SIZE = 1000;
    let page = 0;

    const allStudents: StudentWithEnrolmentsType[] = [];

    while (true) {
      const query = supabaseClient
        .from("students")
        .select(
          `
            id,
            first_name,
            last_name,
            created_at,
            enrolments!inner (
              id,
              campus_id,
              course:course_id(*)
            )
          `,
        )
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      // Filters
      if (filters?.campusesIds?.length) {
        query.in("enrolments.campus_id", filters.campusesIds);
      }

      if (filters?.statuses?.length) {
        query.in("status", filters.statuses);
      }

      // Sorting
      if (sort) {
        if (sort.id === "fullname") {
          query
            .order("first_name", { ascending: !sort.desc })
            .order("last_name", { ascending: !sort.desc });
        } else {
          query.order(sort.id, { ascending: !sort.desc });
        }
      } else {
        query.order("created_at", { ascending: true });
      }

      const { data, error } = await query;
      if (error) throw error;

      if (!data || data.length === 0) break;

      allStudents.push(...data);
      if (data.length < PAGE_SIZE) break;

      page++;
    }

    return allStudents;
  },

  unwrapStudentDEK: async (params: UnwrapStudentDEKType) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.UnwrapStudentDEK,
      opts: {
        body: params,
      },
    });

    if (error || !data) {
      const errorMessage = error?.message;

      sendSentryError({
        message: "Error getting student DEK",
        error: errorMessage,
      });
    }

    return data;
  },

  backfillSINEncryption: async (params: BackfillSINEncryptionType) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.BackfillSINEncryption,
      opts: {
        body: params,
      },
    });

    if (error || !data) {
      throw new Error(
        error?.message ?? "Error running SIN encryption backfill",
      );
    }

    return data;
  },
};
